import { OTP_OPTIONS } from '../../utils/consts';
import { OTP_TEXT, DRAWER_TEXT } from './otp-text-constants';

export interface OTPButtonConfig {
  text: string;
  channel: string;
}

export interface OTPButtonsConfig {
  inline: OTPButtonConfig;
  separate?: OTPButtonConfig;
}

/**
 * Determines the button configurations based on last used OTP channel and available options
 *
 * @param lastUsedOtpChannel - The OTP channel that was last used (sms, phone, email)
 * @param availableOtpOptions - Array of available OTP options
 * @param hasPhone - Whether the user has a phone number on file
 * @returns Configuration object for inline and separate buttons
 */
export const getOTPButtonsConfig = (
  lastUsedOtpChannel: string | undefined,
  availableOtpOptions: string[],
  hasPhone: boolean
): OTPButtonsConfig => {
  const hasEmailOption = availableOtpOptions.includes(OTP_OPTIONS.EMAIL);
  const hasSmsOption = availableOtpOptions.includes(OTP_OPTIONS.SMS);

  // Handle specific last used channels
  if (lastUsedOtpChannel === OTP_OPTIONS.SMS) {
    return {
      inline: { text: OTP_TEXT.TEXT_NEW_CODE, channel: OTP_OPTIONS.SMS },
      separate: { text: OTP_TEXT.CALL_NEW_CODE, channel: OTP_OPTIONS.PHONE }
    };
  }

  if (lastUsedOtpChannel === OTP_OPTIONS.PHONE) {
    return {
      inline: { text: OTP_TEXT.CALL_NEW_CODE, channel: OTP_OPTIONS.PHONE },
      separate: { text: OTP_TEXT.TEXT_NEW_CODE, channel: OTP_OPTIONS.SMS }
    };
  }

  if (lastUsedOtpChannel === OTP_OPTIONS.EMAIL) {
    return {
      inline: { text: OTP_TEXT.EMAIL_NEW_CODE, channel: OTP_OPTIONS.EMAIL }
      // No separate button for email
    };
  }

  // Default behavior when no specific last used channel or first time
  if (hasEmailOption && hasSmsOption) {
    const text = hasPhone ? OTP_TEXT.TEXT_NEW_CODE : OTP_TEXT.CALL_NEW_CODE;
    const channel = hasPhone ? OTP_OPTIONS.SMS : OTP_OPTIONS.PHONE;
    return { inline: { text, channel } };
  }

  if (hasSmsOption) {
    return { inline: { text: OTP_TEXT.TEXT_NEW_CODE, channel: OTP_OPTIONS.SMS } };
  }

  // Default fallback
  return { inline: { text: OTP_TEXT.EMAIL_NEW_CODE, channel: OTP_OPTIONS.EMAIL } };
};

/**
 * Determines the appropriate header text based on the last used OTP channel
 *
 * @param lastUsedOtpChannel - The OTP channel that was last used (sms, phone, email)
 * @param hasPhone - Whether the user has a phone number on file (used for fallback)
 * @returns The appropriate header text string
 */
export const getOTPHeaderText = (
  lastUsedOtpChannel: string | undefined,
  hasPhone: boolean
): string => {
  if (lastUsedOtpChannel === OTP_OPTIONS.SMS) {
    return DRAWER_TEXT.HEADER_PHONE_SMS;
  }
  if (lastUsedOtpChannel === OTP_OPTIONS.PHONE) {
    return DRAWER_TEXT.HEADER_PHONE_CALL;
  }
  if (lastUsedOtpChannel === OTP_OPTIONS.EMAIL) {
    return DRAWER_TEXT.HEADER_EMAIL;
  }
  // Default fallback based on hasPhone when no lastUsedOtpChannel
  return hasPhone ? DRAWER_TEXT.HEADER_PHONE_SMS : DRAWER_TEXT.HEADER_EMAIL;
};
